import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import logo from "../../assets/logobgc.png";
import manualPdf from "../../assets/Procedimiento general SIGER-CLIENTE.pdf";
import createhealthsafety from "../../assets/createhealth&safety.jpg";
import emailbienvenida from "../../assets/emailbienvenida.jpg";
import visualizadordeingreso from "../../assets/visualizadordeingreso.jpg";
import resetpass from "../../assets/resetpass.jpg";
import escritorio from "../../assets/Escritorio.jpeg";
import login from "../../assets/login.png";
import "./Info.css";

const slugify = (text) =>
  String(text)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");

const parseHeadingsFromMarkdown = (markdown) => {
  return markdown
    .split("\n")
    .map((line) => {
      const match = /^(#{1,3})\s+(.*)/.exec(line);
      if (!match) return null;
      const level = match[1].length;
      const text = match[2].trim();
      return {
        level,
        text,
        id: slugify(text),
      };
    })
    .filter(Boolean);
};

const DOC_TEXT = `

## 1. Introducción

Esta guía presenta de forma clara y práctica cómo utilizar el sistema **SIGER**, conocer los distintos perfiles de usuario, entender el flujo de notificaciones automáticas y seguir un paso a paso operativo para aprovechar todas las funcionalidades de la plataforma.

El objetivo es que los usuarios puedan:

- Familiarizarse con el entorno.
- Acceder fácilmente a los informes.
- Descubrir cómo SIGER optimiza la gestión de consumos y costos energéticos.

---

## 2. Perfiles de Usuario y Niveles de Acceso

El sistema SIGER aplica una jerarquía de acceso por niveles:

- **Compañía**
- **Sucursal / Plantas**
- **Áreas / Sectores**

Cada perfil tiene permisos específicos acordes a sus responsabilidades.  
El único acceso permitido hacia niveles superiores es a través del **chat interno**.

### 2.1 Viewer (Visualizador)

**Descripción:** Perfil orientado a consulta.

**Permisos principales:**

- Visualizar informes y tableros de consumos energéticos.
- Acceder al módulo de **Mediciones**.
- Enviar y recibir mensajes mediante el **chat interno** (puede comunicarse con su superior directo).

En la **versión DEMO** el perfil *Viewer* verá de forma demostrativa también los módulos:

- Consumos y Costos (EBM)
- Reportes
- Informes

> En la configuración final de producción los Viewer solo tendrían: **Escritorio, Mediciones e Higiene y Seguridad**.

---

### 2.2 Technical (Técnico)

**Descripción:** Perfil operativo y de mantenimiento.

**Permisos principales:**

- Todos los permisos del perfil **Viewer**.
- ABM (alta, baja, modificación) de **dispositivos**.
- Configuración y monitoreo de **alertas**.
- Visualización de eventos asociados a alertas.
- Acceso a los tableros del módulo **EBM**.
- Comunicación mediante chat con su mismo nivel y superiores.

---

### 2.3 Manager (Gerente / Responsable de Gestión)

**Descripción:** Perfil de gestión, análisis y operaciones vinculadas a costos.

**Permisos principales:**

- Todos los permisos del perfil **Technical**.
- Acceso completo al módulo **EBM**  
  (alta de proveedores, gestión de facturas, etc.).
- Generación y consulta de **reportes e informes** con análisis asistido por IA.
- Comunicación mediante chat con niveles inferiores y superiores.

> Nota: El acceso para **configurar alertas** puede restringirse en Managers según criterio del área técnica y de control de costos.

---

### 2.4 Admin (Administrador de Planta / Sucursal)

**Descripción:** Perfil administrativo local.

**Permisos principales:**

- Todos los permisos del perfil **Manager**.
- Visualización (solo lectura) de la lista de usuarios pertenecientes a su planta y niveles inferiores.
- Comunicación mediante chat con su nivel y superiores.

---

### 2.5 Admin-Master (Administrador General)

**Descripción:** Perfil con control central dentro de su estructura organizacional.

**Permisos principales:**

- Todos los permisos del perfil **Admin**.
- ABM de usuarios (alta, baja, modificación) dentro de su nivel jerárquico y niveles inferiores.
- ABM de plantas/sucursales dentro de su nivel jerárquico y niveles inferiores.
- Comunicación mediante chat con **todos los niveles**.

---

## 3. Notificaciones y Comportamiento al Cargar Informes

1. **Carga de informe por Admin-Master**  
   Cuando un Admin-Master carga un nuevo informe, debe asignar la(s) persona(s) responsables o destinatarios en el campo **“Usuarios a notificar”**.

2. **Notificación por email**  
   Los usuarios asignados reciben automáticamente un correo notificando que se les asignó un nuevo informe e indicando la **fecha de vencimiento**.

3. **Notificación en la plataforma**  
   Al ingresar al sistema, los usuarios asignados verán una notificación en la barra superior (ícono de **campana**) indicando el nuevo informe.

4. **Visibilidad del informe**  
   Todos los usuarios de la **planta** en la que se cargó el informe podrán visualizarlo desde el módulo correspondiente.  
   Sin embargo, el **email de notificación** solo llega a los usuarios que fueron específicamente asignados como destinatarios.

5. **Comportamiento de usuarios DEMO**

   - Al iniciar sesión, los usuarios DEMO son redirigidos automáticamente a la sección **Higiene y Seguridad**, donde verán el listado de informes disponibles y sus notificaciones.
   - Los usuarios DEMO no tienen acceso al resto de los módulos del sistema.  
     Si intentan navegar a otra sección, aparece un **modal** con información de contacto comercial para consultar la contratación de los módulos adicionales de SIGER.

---

## 4. Procedimiento Paso a Paso

A continuación se detalla el flujo operativo para las acciones más habituales: ingreso, consulta de informes, carga de informes y alta de usuarios.

### 4.1 Acceder al sistema (Login)

1. Abrir el navegador y navegar a:  
   \`https://demo.siger.ar/login\`
2. Ingresar **usuario (email)** y **contraseña**.
3. Hacer clic en **“Ingresar”**.
4. Si es la primera vez, ingresar la **contraseña temporal** recibida por email y luego cambiarla en el panel de usuario:  
   \`Perfil → Cambiar contraseña\`.


![Pantalla de login](${login})
---

### 4.2 Qué ver al ingresar (Escritorio)

Al ingresar al sistema, el usuario verá el **escritorio principal**.

1. En la **barra lateral izquierda** están los módulos habilitados según su perfil:

   - Escritorio  
   - Mediciones  
   - Higiene y Seguridad  
   - Consumos y Costos (EBM)  
   - Reportes  
   - Informes  
   - Otros módulos contratados


2. En la **barra superior** se encuentran:

   - Ícono de **campana** (notificaciones)
   - Acceso a **perfil/ajustes**
   - Acceso al **chat interno**

3. Si el usuario es DEMO, al loguearse será redireccionado directamente a **Higiene y Seguridad**.


   ![Escritorio e información del sistema](${escritorio})
---

### 4.3 Consultar notificaciones e informes asignados

1. Hacer clic en el ícono de **campana** en la parte superior para ver notificaciones pendientes.

2. Para acceder al informe asignado:

   - Entrar en **Higiene y Seguridad → Listado de Informes**.
   - Seleccionar el informe deseado.
3. En la ficha del informe se visualizará: título, categoría, fecha del informe, fecha de 
vencimiento, recordatorios y archivo PDF adjunto, haciendo click en el archivo se 
puede descargar. 

![Listado de informes de Higiene y Seguridad](${visualizadordeingreso})

---

### 4.4 Aviso de carga de nuevo informe

Al cargar un **Nuevo Informe** se envía una **notificación automática por email** a todos los usuarios de la planta asignados.

Para esto, en el formulario de carga de informe se deben completar:

- Título del informe.
- Categoría.
- Fecha del informe.
- Fecha de vencimiento.
- Recordatorios (por ejemplo: 15 días, 1 mes, 3 meses antes del vencimiento).
- Usuarios a notificar (emails o usuarios internos).
- Archivo PDF adjunto del informe (descargable por el cliente).


![Carga de nuevo informe](${createhealthsafety})

---

### 4.5 Alta de clientes, plantas y usuarios

**Responsables:** Comercial / Administración / Técnica

**Flujo resumido:**

1. El área **Comercial** informa al equipo de **Administración y Soporte Técnico** que se realizará el alta del cliente.
2. Reunir datos requeridos:
   - Razón social  
   - CUIT  
   - Sucursales  
   - Direcciones físicas  
   - Contactos principales
3. En el sistema se crea la **compañía** (nivel empresa) y se completan los datos generales.
4. Crear las **sucursales/plantas** con dirección y código de oficina proporcionados por el cliente (requerido para el módulo EBM).
5. Opcional: crear **áreas/sectores/zonas locales** para asignaciones específicas.
6. Generar **credenciales temporales** y habilitar el usuario.
7. Enviar **aviso comercial previo** al cliente para evitar que el correo sea filtrado como spam.
8. Confirmar **recepción del email de credenciales** y registro de acceso exitoso.

![Email de alta en SIGER](${emailbienvenida})


![Email de cambio de contraseña exitoso](${resetpass})

---

## 5. Reglas y aclaraciones operativas

- **Visibilidad de informes**  
  Un informe cargado en una planta puede ser visto por **todos los usuarios de esa planta**, pero solo los usuarios **asignados** recibirán la notificación por email.

- **Comunicación hacia arriba**  
  El único canal para comunicarse con niveles superiores es el **chat interno**.  
  Ejemplo: un usuario en “Coto Oficina 1” puede enviar un mensaje a su superior en “Coto Buenos Aires”.

- **Usuarios DEMO**  
  - Tienen acceso restringido principalmente a **Higiene y Seguridad**.  
  - Pueden ver vistas demostrativas de otros módulos.  
  - Si intentan acceder a módulos no contratados, se muestran **modales** con contacto comercial.

- **Cambio de permisos**  
  La asignación de módulos y permisos puede ajustarse según requerimientos comerciales y técnicos, sujeto a aprobación y pruebas.

---

## 6. Comunicación de alta al cliente

Para finalizar el proceso, se comunica al cliente el alta en el sistema.

El cliente recibirá un correo electrónico por parte del equipo de **Nabla Electric S.A.** con el siguiente asunto:

> **Aviso de alta en Sistema SIGER – Nabla Electric S.A.**

En ese correo se detallan:

- Acceso a la plataforma.
- Usuario asignado.
- Contraseña temporal.
- Recomendación de cambio de contraseña en el primer ingreso.

---
`;

export default function Info() {
  const headings = useMemo(() => parseHeadingsFromMarkdown(DOC_TEXT), []);

  const scrollToHeading = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (!headings.length) return;

    const headingElements = Array.from(
      document.querySelectorAll(
        ".markdown-doc h1, .markdown-doc h2, .markdown-doc h3"
      )
    );
    if (!headingElements.length) return;
  }, [headings]);

  const HeadingRenderer = ({ level, children, ...props }) => {
    const text = React.Children.toArray(children)
      .map((child) => (typeof child === "string" ? child : ""))
      .join("");
    const id = slugify(text);
    const Tag = `h${level}`;
    return (
      <Tag id={id} {...props}>
        {children}
      </Tag>
    );
  };

  const handleDownloadManual = () => {
    const link = document.createElement("a");
    link.href = manualPdf;
    link.download = "Procedimiento general SIGER-CLIENTE.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container cont-info-doc">
      <div className="info-doc-card">
        <header className="info-doc-header">
          <div className="info-doc-title">
            <img src={logo} alt="Logo BGC" className="info-doc-logo" />
            <div className="info-doc-title-text">
              <h6> Guía Interactiva SIGER</h6>

              <div className="info-doc-subrow">
                <span>Conoce cómo gestionar, visualizar y optimizar tu información energética.  </span>
                <button
                  type="button"
                  className="info-doc-download"
                  onClick={handleDownloadManual}
                >
                  Descargar manual
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="info-doc-main">
          {headings.length > 0 && (
            <nav className="info-doc-toc">
              <span className="info-doc-toc-title">Índice</span>
              <ul>
                {headings.map((h) => (
                  <li
                    key={h.id}
                    className={`toc-item level-${h.level}`}
                  >
                    <button type="button" onClick={() => scrollToHeading(h.id)}>
                      {h.text}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <article className="info-doc-body">
            <div className="markdown-doc">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => (
                    <HeadingRenderer level={1} {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <HeadingRenderer level={2} {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <HeadingRenderer level={3} {...props} />
                  ),
                }}
              >
                {DOC_TEXT}
              </ReactMarkdown>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}
